import {
  Box,
  Button,
  Container,
  Form,
  Header,
  SpaceBetween,
} from "@cloudscape-design/components";
import { FC, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm, Control } from "react-hook-form";
import FormSelect from "../../../components/ControlledFormInputs/Select";
import FormInput from "../../../components/ControlledFormInputs/Input";
import LoadingSpinner from "../../../components/LoadingSpinner";
import useApplications from "../../../hooks/query/useApplications";
import { useNewGcpCertsFormStore } from "../../../stores/gcpCerts/newGcpCertsFormStore";
import { GcpCertRequest, getGcpCertApprovalObject } from "../../../types/gcpCert";
import { useOktaAuth } from "@okta/okta-react";
import useCreateNewGcpCert from "../../../hooks/mutate/useCreateNewGcpCert";

type SelectOption = { label: string; value: string };

const CERTIFICATE_TYPE_OPTIONS: SelectOption[] = [
  { label: "SSL certificates/Classic Certificates", value: "ssl_classic" },
  { label: "Self-managed certificates/certificatev2", value: "self_managed_v2" },
];

const FormInputContainer = ({ children }: { children: React.ReactNode }) => (
  <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
    <div style={{ width: "650px" }}>{children}</div>
  </div>
);

const NewGcpCerts: FC = () => {
  const navigate = useNavigate();
  const { data: applications, isLoading: isApplicationsLoading } = useApplications();
  const { authState } = useOktaAuth();
  const thisUserEmail = authState?.idToken?.claims.email as string;

  const {
    formValues: newGcpCertFormValues,
    actions: { setFormValues: setNewGcpCertFormValues, clearFormValues },
  } = useNewGcpCertsFormStore();

  const { control, setValue, watch, trigger, formState } = useForm({
    defaultValues: newGcpCertFormValues,
  });

  const { mutate: createResource, isLoading: isSendLoading } = useCreateNewGcpCert();

  const [dnsPrefixInput, setDnsPrefixInput] = useState("");
  const [duplicateError, setDuplicateError] = useState("");

  const getDnsDomainFromProjectId = (projectId: string) => {
    if (!projectId) return "";
    const parts = projectId.split("-");
    const platformAlias = parts[2] || "";
    const env = parts[parts.length - 1] || "";
    const envPart = ["dev", "stg", "sbx"].includes(env) ? `-${env}` : "";
    return `${platformAlias}${envPart}.gcp.globetel.cloud.`;
  };

  const formatName = (name: string) =>
    name
      ?.split("-")
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(" ") || "";

  // --- Prepare approved applications with environments ---
  const approvedAppOptions = useMemo(() => {
    if (!applications) return [];

    const map: Record<
      string,
      {
        label: string;
        value: string;
        environments: SelectOption[];
        envDetails: any[];
      }
    > = {};

    applications
      .filter((a) => a.status === "APPROVED" && a.details.applicationAlias)
      .forEach((a) => {
        const alias = a.details.applicationAlias!;
        const envValue = a.details.environment?.value || "unknown";
        const envLabel = a.details.environment?.label || envValue;

        if (!map[alias]) {
          map[alias] = {
            label: a.details.applicationName,
            value: alias,
            environments: [],
            envDetails: [],
          };
        }

        map[alias].environments.push({ label: envLabel, value: envValue });
        map[alias].envDetails.push({
          env: envValue,
          projectId: (a as any).projectId,
          platformLabel: a.details.platformName?.label,
          applicationOwner: a.details.applicationOwner ?? a.details.applicationOwnerName,
          applicationOwnerEmail: a.details.applicationOwnerEmail,
          costCenter: a.details.costCenter || "NA",
          wbsCode: a.details.wbsCode || "NA",
        });
      });

    return Object.values(map);
  }, [applications]);

  const selectedAppAlias = watch("application_alias");
  const selectedEnv = watch("environment");

  // --- Update environment dropdown when app changes ---
  useEffect(() => {
    if (!applications || !selectedAppAlias) return;

    const aliasValue =
      typeof selectedAppAlias === "string"
        ? selectedAppAlias
        : (selectedAppAlias as SelectOption).value;

    const approvedSelectedApps = applications.filter(
      (app) =>
        app.status === "APPROVED" && app.details.applicationAlias === aliasValue
    );

    const envOptions = approvedSelectedApps
      .map((app) => {
        const env = app.details?.environment?.value;
        return env ? { label: env.toUpperCase(), value: env } : null;
      })
      .filter((e): e is SelectOption => Boolean(e));

    setValue("environment", "");
    setNewGcpCertFormValues("environment", "");
    setNewGcpCertFormValues("envOptions", envOptions);
  }, [selectedAppAlias, applications, setValue, setNewGcpCertFormValues]);

  // --- Auto-fill form fields when environment changes ---
  useEffect(() => {
    if (!selectedAppAlias || !selectedEnv) return;

    const aliasValue =
      typeof selectedAppAlias === "string"
        ? selectedAppAlias
        : (selectedAppAlias as SelectOption).value;

    const envValue =
      typeof selectedEnv === "string"
        ? selectedEnv
        : (selectedEnv as SelectOption).value;

    const app = approvedAppOptions.find((a) => a.value === aliasValue);
    if (!app) return;

    const envDetails = app.envDetails.find((e: any) => e.env === envValue);
    if (!envDetails) return;

    const updates = {
      platform_name: envDetails.platformLabel,
      project_id: envDetails.projectId,
      application_owner: formatName(envDetails.applicationOwner),
      application_owner_email: envDetails.applicationOwnerEmail,
      wbs_code: envDetails.wbsCode,
      cost_center: envDetails.costCenter,
      dns_domain_name: getDnsDomainFromProjectId(envDetails.projectId),
    };

    Object.entries(updates).forEach(([k, v]) => {
      setValue(k, v);
      setNewGcpCertFormValues(k, v);
    });
  }, [selectedAppAlias, selectedEnv, approvedAppOptions]);

  if (isApplicationsLoading) return <LoadingSpinner />;

  const selectedApp = approvedAppOptions.find(
    (a) => a.value === selectedAppAlias
  );
  const envOptions =
    newGcpCertFormValues.envOptions?.length
      ? newGcpCertFormValues.envOptions
      : selectedApp?.environments || [];

  const isSubmitDisabled =
    isSendLoading ||
    !newGcpCertFormValues.application_alias ||
    !newGcpCertFormValues.environment ||
    !newGcpCertFormValues.certificate_type ||
    !(newGcpCertFormValues.subdomain_requests?.length > 0) ||
    !newGcpCertFormValues.project_id ||
    !newGcpCertFormValues.dns_domain_name;

  const handleSubmit = async () => {
    if (isSubmitDisabled) return;
    const isValid = await trigger();
    if (!isValid) return;

    const newGcpCert: GcpCertRequest = {
      status: "PENDING_APPROVAL",
      requestorID: thisUserEmail,
      action: "CREATE",
      details: {
        applicationAlias: newGcpCertFormValues.application_alias,
        environment: newGcpCertFormValues.environment,
        platformName: newGcpCertFormValues.platform_name,
        projectId: newGcpCertFormValues.project_id,
        applicationOwner: newGcpCertFormValues.application_owner,
        applicationOwnerEmail: newGcpCertFormValues.application_owner_email,
        wbsCode: newGcpCertFormValues.wbs_code,
        costCenter: newGcpCertFormValues.cost_center,
        dnsDomainName: newGcpCertFormValues.dns_domain_name,
        dnsPrefixes: (newGcpCertFormValues.subdomain_requests || []).map(
          (sd: { prefix: string }) => sd.prefix
        ),
        ...(newGcpCertFormValues.certificate_type && {
          certificateType: newGcpCertFormValues.certificate_type,
        }),
      },
      approvalStatus: getGcpCertApprovalObject(["GCP_TRIBE", "APPLICATION_OWNER"]),
    };

    createResource(newGcpCert, {
      onSuccess: () => {
        clearFormValues();
        navigate(`/gcpCerts`);
      },
    });
  };

  const handleCancel = () => {
    clearFormValues();
    navigate('/gcpCerts');
  };

  return (
    <Form
      header={<Header variant="h1">New GCP Certificate Request</Header>}
      actions={
        <SpaceBetween direction="horizontal" size="xs">
          <Box>
            <Button variant="primary" onClick={handleSubmit} loading={isSendLoading} disabled={isSubmitDisabled}>
              Submit
            </Button>
            <Button variant="link" onClick={handleCancel}>
              Cancel
            </Button>
          </Box>
        </SpaceBetween>
      }
    >
      <Container>
        <SpaceBetween direction="vertical" size="l">
          <FormInputContainer>
            <FormSelect
              control={control as unknown as Control<any>}
              setFormValues={setNewGcpCertFormValues}
              name="application_alias"
              label="Application Name"
              placeholder="Select Application"
              options={approvedAppOptions.map((a) => ({
                label: a.label,
                value: a.value,
              }))}
              rules={{ required: "Please select an Application" }}
            />
          </FormInputContainer>

          <FormInputContainer>
            <FormSelect
              control={control as unknown as Control<any>}
              setFormValues={setNewGcpCertFormValues}
              name="environment"
              label="Environment"
              placeholder="Select Environment"
              options={envOptions}
              rules={{ required: "Please select an Environment" }}
            />
          </FormInputContainer>

          <FormInputContainer>
            <FormSelect
              control={control as unknown as Control<any>}
              setFormValues={setNewGcpCertFormValues}
              name="certificate_type"
              label="Certificate Type"
              placeholder="Select Certificate Type"
              options={CERTIFICATE_TYPE_OPTIONS}
              rules={{ required: "Please select a Certificate Type" }}
            />
          </FormInputContainer>

          {[
            { name: "platform_name", label: "Platform" },
            { name: "project_id", label: "Project ID" },
            { name: "application_owner", label: "Application Owner" },
            { name: "application_owner_email", label: "Application Owner Email" },
            { name: "wbs_code", label: "WBS Code" },
            { name: "cost_center", label: "Cost Center" },
            { name: "dns_domain_name", label: "Hosted Zone" },
          ].map((f) => (
            <FormInputContainer key={f.name}>
              <FormInput
                control={control as unknown as Control<any>}
                setFormValues={setNewGcpCertFormValues}
                name={f.name}
                label={f.label}
                disabled
              />
            </FormInputContainer>
          ))}

          <FormInputContainer>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <input
                  type="text"
                  placeholder="Enter subdomain prefix"
                  value={dnsPrefixInput}
                  onChange={(e) => {
                    setDnsPrefixInput(e.target.value);
                    setDuplicateError("");
                  }}
                  style={{
                    flex: 1,
                    padding: "8px",
                    borderRadius: "4px",
                    border: "1px solid #ccc",
                  }}
                />
                <Button
                  onClick={() => {
                    if (!dnsPrefixInput) return;
                    const baseDomain =
                      watch("dns_domain_name") || newGcpCertFormValues.dns_domain_name;
                    const fullDomain = `${dnsPrefixInput}.${baseDomain}`;
                    const isDuplicate = newGcpCertFormValues.subdomain_requests.some(
                      (sd) => sd.prefix === dnsPrefixInput
                    );
                    if (isDuplicate) {
                      setDuplicateError("This subdomain prefix has already been added.");
                      return;
                    }
                    setNewGcpCertFormValues("subdomain_requests", [
                      ...(newGcpCertFormValues.subdomain_requests || []),
                      { prefix: dnsPrefixInput, fullDomain },
                    ]);
                    setDnsPrefixInput("");
                  }}
                >
                  Add
                </Button>
                <Button
                  variant="link"
                  onClick={() => setNewGcpCertFormValues("subdomain_requests", [])}
                >
                  Clear All
                </Button>
              </div>
              {duplicateError && (
                <div style={{ color: "red", fontSize: "12px" }}>{duplicateError}</div>
              )}
              {newGcpCertFormValues.subdomain_requests.length > 0 && (
                <div style={{ marginTop: "8px", display: "flex", flexDirection: "column", gap: "4px" }}>
                  {newGcpCertFormValues.subdomain_requests.map(
                    (sd: { prefix: string; fullDomain?: string }) => {
                      const baseDomain =
                        watch("dns_domain_name") || newGcpCertFormValues.dns_domain_name;
                      const fullDomain = `${sd.prefix}.${baseDomain}`;
                      return (
                        <div
                          key={sd.prefix}
                          style={{
                            padding: "4px 8px",
                            border: "1px solid #ddd",
                            borderRadius: "4px",
                            background: "#f9f9f9",
                          }}
                        >
                          {fullDomain}
                        </div>
                      );
                    }
                  )}
                </div>
              )}
            </div>
          </FormInputContainer>
        </SpaceBetween>
      </Container>
    </Form>
  );
};

export default NewGcpCerts;